import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute, RouterLink } from '@angular/router';
import { FieldTestSubmissionService } from 'src/app/core/data-services/field-test-submission.service';
import { CustomFieldTestRequest } from 'src/app/core/request-models/custom-field-test-request.model';
import { CustomFieldTest } from 'src/app/core/data-models/custom-field-test.model';
import { clearableFieldValue } from 'src/app/core/utils/drone-request-utils';
import { blankToUndefined } from 'src/app/core/utils/field-test-utils';
import { DroneService } from 'src/app/core/data-services/drone.service';
import { toCdnUrl } from 'src/app/shared/utils/format.utils';
import { MaxLengthDirective } from 'src/app/shared/directives/max-length.directive';
import { CharCounterDirective } from 'src/app/shared/directives/char-counter.directive';
import { openExternal } from '../../../../../core/utils/external-link-utils';

interface UploadedFile {
  id?: string; // File ID from database (present for existing files)
  name: string;
  size: number;
  type: string;
  filePath?: string; // Path only (not full URL) - use staticCdn pipe for display
  uploadProgress?: number;
  uploaded?: boolean;
  file?: File; // Store the actual file for upload
  isNew?: boolean; // Track if this is a newly added file (for edit mode)
}

@Component({
  selector: 'app-custom-field-test-form',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterLink, MaxLengthDirective, CharCounterDirective],
  templateUrl: './custom-field-test-form.component.html',
  styleUrls: ['./custom-field-test-form.component.scss']
})
export class CustomFieldTestFormComponent implements OnInit {
  private submissionService = inject(FieldTestSubmissionService);
  private droneService = inject(DroneService);

  // Expose drones for the template
  readonly drones = this.droneService.drones;

  testForm!: FormGroup;
  isEditMode = false;
  /** The submission as loaded in edit mode, so emptied optional fields are sent as `null`. */
  private originalTest: CustomFieldTest | null = null;
  isLoading = false;
  isSaving = false;
  testId: string | null = null;
  today = new Date().toISOString().split('T')[0]; // For max date validation

  // File upload
  uploadedFiles: UploadedFile[] = [];
  isDragOver = false;
  maxFileSize = 25 * 1024 * 1024; // 25MB
  acceptedTypes = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ];

  // Test type options - compliance-focused
  testTypes = [
    { value: 'nozzle-calibration', label: 'Nozzle Calibration', icon: 'bi-droplet-half', description: 'Spray nozzle flow rate and pattern tests' },
    { value: 'equipment-inspection', label: 'Equipment Inspection', icon: 'bi-clipboard-check', description: 'General equipment inspection form' },
    { value: 'spray-pattern', label: 'Spray Pattern Test', icon: 'bi-grid-3x3', description: 'Spray coverage and distribution test' },
    { value: 'flow-rate', label: 'Flow Rate Verification', icon: 'bi-speedometer2', description: 'Application rate calibration test' },
    { value: 'tank-calibration', label: 'Tank Calibration', icon: 'bi-bucket', description: 'Tank volume and measurement test' },
    { value: 'other', label: 'Other', icon: 'bi-file-earmark', description: 'Other field test document' }
  ];

  // Category options - seasonal
  categories = [
    { value: 'pre-season', label: 'Pre-Season' },
    { value: 'in-season', label: 'In-Season' },
    { value: 'post-season', label: 'Post-Season' },
    { value: 'maintenance', label: 'Maintenance' }
  ];

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.testId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = !!this.testId;

    this.initializeForm();
    this.loadDrones();

    if (this.isEditMode && this.testId) {
      this.loadExistingTest();
    }
  }

  /**
   * Load drones from the API for the aircraft dropdown
   */
  async loadDrones(): Promise<void> {
    try {
      await this.droneService.loadOrganizationDrones();
    } catch (error) {
      console.error('Error loading drones:', error);
    }
  }

  initializeForm(): void {
    this.testForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3)]],
      testType: ['nozzle-calibration', Validators.required],
      category: ['', Validators.required],
      datePerformed: ['', Validators.required],
      description: [''],
      applicableAircraft: ['all'],
      frequency: ['as-needed'],
      requiresSignoff: [false]
    });
  }

  // Validate date is not in future
  isDateInFuture(): boolean {
    const dateValue = this.testForm.get('datePerformed')?.value;
    if (!dateValue) return false;
    return new Date(dateValue) > new Date();
  }

  // Drag and drop handlers
  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files);
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
    // Reset input so same file can be selected again
    input.value = '';
  }

  handleFiles(files: FileList): void {
    Array.from(files).forEach(file => {
      // Validate file type
      if (!this.acceptedTypes.includes(file.type)) {
        alert(`File type not supported: ${file.name}. Please upload PDF, Word, or image files.`);
        return;
      }

      // Validate file size
      if (file.size > this.maxFileSize) {
        alert(`File too large: ${file.name}. Maximum size is 25MB.`);
        return;
      }

      // Check for duplicates
      if (this.uploadedFiles.some(f => f.name === file.name)) {
        alert(`File already added: ${file.name}`);
        return;
      }

      // Add file and start real upload
      const uploadedFile: UploadedFile = {
        name: file.name,
        size: file.size,
        type: file.type,
        uploadProgress: 0,
        uploaded: false,
        file: file, // Store the actual file
        isNew: true // Mark as new file (important for edit mode)
      };

      this.uploadedFiles.push(uploadedFile);
      this.uploadFileToS3(uploadedFile);
    });
  }

  /**
   * Upload file to S3 using pre-signed URL
   */
  async uploadFileToS3(uploadedFile: UploadedFile): Promise<void> {
    if (!uploadedFile.file) return;

    try {
      // Step 1: Get pre-signed upload URL
      uploadedFile.uploadProgress = 10;
      const urlResponse = await this.submissionService.getUploadUrl(
        uploadedFile.name,
        uploadedFile.type
      );

      if (!urlResponse) {
        throw new Error('Failed to get upload URL');
      }

      uploadedFile.uploadProgress = 30;

      // Step 2: Upload file to S3
      const uploadSuccess = await this.submissionService.uploadFile(
        urlResponse.uploadUrl,
        uploadedFile.file
      );

      if (!uploadSuccess) {
        throw new Error('Failed to upload file to S3');
      }

      // Step 3: Mark as complete
      uploadedFile.uploadProgress = 100;
      uploadedFile.uploaded = true;
      uploadedFile.filePath = urlResponse.filePath;
    } catch (error) {
      console.error('Error uploading file:', error);
      // Remove the failed file from the list
      const index = this.uploadedFiles.indexOf(uploadedFile);
      if (index > -1) {
        this.uploadedFiles.splice(index, 1);
      }
      alert(`Failed to upload ${uploadedFile.name}. Please try again.`);
    }
  }

  async removeFile(index: number): Promise<void> {
    const file = this.uploadedFiles[index];

    // If this is an existing file (has ID and is in edit mode), call API to delete
    if (file.id && this.isEditMode && this.testId) {
      const confirmed = confirm(`Are you sure you want to remove "${file.name}"? This cannot be undone.`);
      if (!confirmed) {
        return;
      }

      const success = await this.submissionService.removeFileFromCustomTest(this.testId, file.id);
      if (!success) {
        alert('Failed to remove file. Please try again.');
        return;
      }
    }

    // Remove from local array
    this.uploadedFiles.splice(index, 1);
  }

  getFileIcon(type: string): string {
    if (type === 'application/pdf') return 'bi-file-earmark-pdf';
    if (type.startsWith('image/')) return 'bi-file-earmark-image';
    if (type.includes('word')) return 'bi-file-earmark-word';
    return 'bi-file-earmark';
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  async loadExistingTest(): Promise<void> {
    if (!this.testId) return;

    this.isLoading = true;

    try {
      const test = await this.submissionService.fetchCustomTestById(this.testId);

      if (test) {
        this.originalTest = test;
        // Extract date from timestamp if needed
        const datePerformed = test.datePerformed ? test.datePerformed.split('T')[0] : '';

        this.testForm.patchValue({
          title: test.title,
          testType: test.testType,
          category: test.category || '',
          datePerformed: datePerformed,
          description: test.description || '',
          applicableAircraft: test.applicableAircraft || 'all',
          frequency: test.frequency || 'as-needed',
          requiresSignoff: test.requiresSignoff || false
        });

        // Load existing files (include ID for delete operations)
        this.uploadedFiles = (test.files || []).map(f => ({
          id: f.id,  // Include file ID for delete operations
          name: f.name,
          size: f.size || 0,
          type: f.type,
          filePath: f.filePath,
          uploadProgress: 100,
          uploaded: true,
          isNew: false  // Existing files are not new
        }));
      } else {
        console.error('Custom field test not found');
        this.router.navigate(['/logbook/testing/field']);
      }
    } catch (error) {
      console.error('Error loading custom field test:', error);
      this.router.navigate(['/logbook/testing/field']);
    } finally {
      this.isLoading = false;
    }
  }

  async onSubmit(): Promise<void> {
    if (this.testForm.invalid) {
      this.testForm.markAllAsTouched();
      return;
    }

    if (this.uploadedFiles.length === 0) {
      alert('Please upload at least one file for this test.');
      return;
    }

    // Check if all files are uploaded
    if (this.uploadedFiles.some(f => !f.uploaded)) {
      alert('Please wait for all files to finish uploading.');
      return;
    }

    this.isSaving = true;

    try {
      const formValue = this.testForm.value;

      // Build file array for API
      const files = this.uploadedFiles
        .filter(f => f.uploaded && f.filePath)
        .map(f => ({
          fileName: f.name,
          fileType: f.type,
          filePath: f.filePath!,
          fileSize: f.size
        }));

      if (this.isEditMode && this.testId) {
        // Update existing custom test metadata
        const result = await this.submissionService.updateCustomTest(this.testId, {
          title: formValue.title,
          testType: formValue.testType,
          category: clearableFieldValue(formValue.category, this.originalTest?.category),
          description: clearableFieldValue(formValue.description, this.originalTest?.description),
          applicableAircraft: formValue.applicableAircraft,
          frequency: formValue.frequency,
          requiresSignoff: formValue.requiresSignoff
        });

        if (result) {
          // Add any new files that were uploaded during edit
          const newFiles = this.uploadedFiles.filter(f => f.isNew && f.uploaded && f.filePath);
          for (const file of newFiles) {
            await this.submissionService.addFileToCustomTest(this.testId, {
              fileName: file.name,
              fileType: file.type,
              filePath: file.filePath!,
              fileSize: file.size
            });
          }

          this.router.navigate(['/logbook/testing/field']);
        } else {
          alert('Failed to update field test. Please try again.');
        }
      } else {
        // Create new custom test with files
        const dto: CustomFieldTestRequest = {
          title: formValue.title,
          testType: formValue.testType,
          category: blankToUndefined(formValue.category),
          description: blankToUndefined(formValue.description),
          applicableAircraft: formValue.applicableAircraft,
          frequency: formValue.frequency,
          requiresSignoff: formValue.requiresSignoff,
          files: files
        };

        const result = await this.submissionService.createCustomTest(dto);

        if (result) {
          this.router.navigate(['/logbook/testing/field']);
        } else {
          alert('Failed to save field test. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error saving custom field test:', error);
      alert('An error occurred while saving. Please try again.');
    } finally {
      this.isSaving = false;
    }
  }

  onCancel(): void {
    this.router.navigate(['/logbook/testing/field']);
  }

  getTestTypeIcon(type: string): string {
    return this.testTypes.find(t => t.value === type)?.icon || 'bi-file-earmark';
  }

  getTestTypeLabel(): string {
    const type = this.testForm.get('testType')?.value;
    return this.testTypes.find(t => t.value === type)?.label || '';
  }

  getCategoryLabel(): string {
    const category = this.testForm.get('category')?.value;
    return this.categories.find(c => c.value === category)?.label || '';
  }

  /**
   * Open file in new tab via CDN with signed URL query params
   */
  async openFile(filePath: string | undefined): Promise<void> {
    if (!filePath) {
      console.error('No file path available');
      return;
    }

    try {
      const signedUrl = await this.submissionService.getDownloadUrl(filePath);
      if (signedUrl) {
        openExternal(toCdnUrl(signedUrl));
      } else {
        console.error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Error opening file:', error);
    }
  }

}
